// Client View helpers — pure deterministic functions used by the React
// components. No network, no React, no LLM.

export const CATEGORY_ORDER = [
    "ADVANTAGE",
    "OPPORTUNITY",
    "THREAT",
    "CATCH_UP",
    "BLIND_SPOT",
    "MONITOR",
];

const CATEGORY_RANK = CATEGORY_ORDER.reduce((acc, c, i) => {
    acc[c] = i;
    return acc;
}, {});

export const CATEGORY_LABEL = {
    ADVANTAGE: "Advantage",
    OPPORTUNITY: "Opportunity",
    THREAT: "Threat",
    CATCH_UP: "Catch-up",
    BLIND_SPOT: "Blind spot",
    MONITOR: "Monitor",
};

export const CATEGORY_TONE = {
    ADVANTAGE: "positive",
    OPPORTUNITY: "accent",
    THREAT: "negative",
    CATCH_UP: "warning",
    BLIND_SPOT: "muted",
    MONITOR: "muted",
};

const LEVEL_RANK = { VERY_HIGH: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };

export const levelRank = (level) => LEVEL_RANK[(level || "").toUpperCase()] || 0;

export const levelLabel = (level) => {
    const u = (level || "").toUpperCase();
    if (u === "VERY_HIGH") return "Very high";
    if (u === "HIGH") return "High";
    if (u === "MEDIUM") return "Medium";
    if (u === "LOW") return "Low";
    return level || "—";
};

export const levelTone = (level) => {
    const u = (level || "").toUpperCase();
    if (u === "VERY_HIGH") return "ink";
    if (u === "HIGH") return "ink";
    if (u === "MEDIUM") return "warning";
    if (u === "LOW") return "muted";
    return "muted";
};

const num = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : 0);

/** Sort track cards: card category rank → client.relevanceScore desc →
 *  client.urgency level desc → stableTrackName asc. */
export const sortTrackCards = (cards) => {
    if (!Array.isArray(cards)) return [];
    return [...cards].sort((a, b) => {
        const ra = CATEGORY_RANK[(a?.client?.cardCategory || "").toUpperCase()] ?? 99;
        const rb = CATEGORY_RANK[(b?.client?.cardCategory || "").toUpperCase()] ?? 99;
        if (ra !== rb) return ra - rb;
        const rsA = num(a?.client?.relevanceScore);
        const rsB = num(b?.client?.relevanceScore);
        if (rsA !== rsB) return rsB - rsA;
        const uA = levelRank(a?.client?.urgency);
        const uB = levelRank(b?.client?.urgency);
        if (uA !== uB) return uB - uA;
        return String(a?.stableTrackName || "").localeCompare(String(b?.stableTrackName || ""));
    });
};

/** Stable composite identity for a track card *within a (clientId, week)*.
 *  Used as the dedupe key AND the React key for all signal-board rows. */
export const trackCardKey = (card, { clientId = "", selectedWeek = "" } = {}) => {
    if (!card || typeof card !== "object") return "";
    const id = card.canonicalTrackId || card.trackId || card.stableTrackName || "";
    return `${clientId}|${selectedWeek}|${id}`;
};

/** Deterministically drop duplicate track cards inside a (clientId, week).
 *  Tie-break priority (kept card wins):
 *    1. approvedForDashboard === true
 *    2. higher client.relevanceScore
 *    3. higher market.score
 *    4. newer createdAt (ISO compare)
 *    5. stable fallback by trackId asc (insertion order tiebreaker)
 *  Logs `console.warn("Dropped duplicate client trackCards", {…})` once per
 *  collapsed group when duplicates are dropped. Returns a NEW array; original
 *  is untouched. */
export const dedupeTrackCards = (cards, ctx = {}) => {
    if (!Array.isArray(cards) || cards.length === 0) return [];
    const groups = new Map(); // key → array of cards in insertion order
    for (const card of cards) {
        if (!card || typeof card !== "object") continue;
        const key = trackCardKey(card, ctx);
        if (!key) continue;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(card);
    }

    const out = [];
    for (const [key, list] of groups) {
        if (list.length === 1) {
            out.push(list[0]);
            continue;
        }
        // Pick the strongest survivor via the documented priority order.
        const ranked = [...list].sort((a, b) => {
            // 1. approvedForDashboard true wins
            const aa = a?.approvedForDashboard === true ? 1 : 0;
            const bb = b?.approvedForDashboard === true ? 1 : 0;
            if (aa !== bb) return bb - aa;
            // 2. higher client.relevanceScore
            const ra = num(a?.client?.relevanceScore);
            const rb = num(b?.client?.relevanceScore);
            if (ra !== rb) return rb - ra;
            // 3. higher market.score
            const ma = num(a?.market?.score);
            const mb = num(b?.market?.score);
            if (ma !== mb) return mb - ma;
            // 4. newer createdAt (ISO string compare works for ISO-8601)
            const ca = String(a?.createdAt || "");
            const cb = String(b?.createdAt || "");
            if (ca !== cb) return cb.localeCompare(ca);
            // 5. deterministic fallback: trackId asc keeps the first by id
            return String(a?.trackId || "").localeCompare(String(b?.trackId || ""));
        });
        const kept = ranked[0];
        const dropped = ranked.slice(1);
        out.push(kept);
        // Soft warning so we notice if the upstream regresses
        if (typeof console !== "undefined" && typeof console.warn === "function") {
            console.warn("Dropped duplicate client trackCards", {
                clientId: ctx.clientId || null,
                selectedWeek: ctx.selectedWeek || null,
                key,
                keptTrackId: kept?.trackId,
                keptRelevanceScore: kept?.client?.relevanceScore,
                keptMarketScore: kept?.market?.score,
                droppedCount: dropped.length,
                droppedTrackIds: dropped.map((d) => d?.trackId),
                droppedRelevanceScores: dropped.map((d) => d?.client?.relevanceScore),
                droppedMarketScores: dropped.map((d) => d?.market?.score),
            });
        }
    }
    return out;
};

/** Build a `businessSegment × capabilityArea` matrix from an impactMatrix
 *  list. Rows and columns are derived from the data so each client gets its
 *  own dimensions. Cell contains the highest-impact row plus the full list of
 *  matching rows for the click-through panel. */
export const buildImpactMatrix = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return { businessSegments: [], capabilityAreas: [], cells: {}, count: 0 };
    }
    const segmentTotals = new Map();
    const capabilityTotals = new Map();
    const cells = {};
    for (const r of rows) {
        if (!r || typeof r !== "object") continue;
        const bs = (r.businessSegment || "").trim();
        const ca = (r.capabilityArea || "").trim();
        if (!bs || !ca) continue;
        const lvl = levelRank(r.impactLevel);
        segmentTotals.set(bs, (segmentTotals.get(bs) || 0) + lvl);
        capabilityTotals.set(ca, (capabilityTotals.get(ca) || 0) + lvl);
        const key = `${bs}__${ca}`;
        if (!cells[key]) cells[key] = { businessSegment: bs, capabilityArea: ca, rows: [] };
        cells[key].rows.push(r);
    }
    for (const key of Object.keys(cells)) {
        const c = cells[key];
        c.rows.sort((a, b) => {
            const dl = levelRank(b.impactLevel) - levelRank(a.impactLevel);
            if (dl !== 0) return dl;
            return (CATEGORY_RANK[(a.impactType || "").toUpperCase()] ?? 99) - (CATEGORY_RANK[(b.impactType || "").toUpperCase()] ?? 99);
        });
        const top = c.rows[0];
        c.top = top;
        c.impactLevel = top?.impactLevel || null;
        c.impactType = top?.impactType || null;
    }
    const businessSegments = [...segmentTotals.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([k]) => k);
    const capabilityAreas = [...capabilityTotals.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([k]) => k);
    return { businessSegments, capabilityAreas, cells, count: rows.length };
};

/** Build a matrix that uses the full client axes (derived from `allRows`)
 *  but only populates cells from `scopedRows`. Empty cells for the scoped
 *  view remain in the matrix as `undefined`, so the UI can render them as a
 *  quiet "low / none" placeholder.  */
export const buildScopedMatrix = ({ allRows, scopedRows }) => {
    const axes = buildImpactMatrix(allRows || []);
    const scoped = buildImpactMatrix(scopedRows || []);
    return {
        businessSegments: axes.businessSegments,
        capabilityAreas: axes.capabilityAreas,
        cells: scoped.cells,
        count: scoped.count,
        axesCount: axes.count,
    };
};

// ---------------------------------------------------------------------------
// Organizational Exposure matrix (primary builder)
// ---------------------------------------------------------------------------
// Spec — see prompt:
//   - The matrix must always be the FULL client-specific grid.
//   - Capability areas render as VERTICAL rows.
//   - Business segments render as HORIZONTAL columns.
//   - Axes come from clientProfile.matrixConfig / clientProfile.businessSegments
//     + clientProfile.capabilityAreas — preserved in the order configured by
//     the client, NEVER reordered by impact.
//   - Cells come from `organizationalExposureMatrix` if present, otherwise
//     from the flat `impactMatrix` fallback.
//   - The scope toggle filters cell.impacts[] but never removes axes.
// ---------------------------------------------------------------------------

const ORG_LEVEL_FALLBACK = "NO_IMPACT";

const _uniqStrings = (xs) => {
    const seen = new Set();
    const out = [];
    for (const x of xs || []) {
        const v = (x == null ? "" : String(x)).trim();
        if (!v) continue;
        if (seen.has(v)) continue;
        seen.add(v);
        out.push(v);
    }
    return out;
};

/** Extract a label string from an axis entry. The upstream schema is
 *  inconsistent: sometimes a flat array of strings, sometimes an array of
 *  objects like `{segment}` or `{capabilityArea}`. We look at the well-known
 *  field for the requested axis kind first, then generic `name`/`label`/`id`. */
const _extractAxisLabel = (entry, kind) => {
    if (entry == null) return "";
    if (typeof entry === "string") return entry.trim();
    if (typeof entry === "object") {
        const candidates = kind === "businessSegment"
            ? ["segment", "businessSegment", "name", "label", "title", "id"]
            : ["capabilityArea", "capability", "area", "name", "label", "title", "id"];
        for (const k of candidates) {
            if (typeof entry[k] === "string" && entry[k].trim()) return entry[k].trim();
        }
        return "";
    }
    return String(entry).trim();
};

const _axisStrings = (xs, kind) => {
    const seen = new Set();
    const out = [];
    for (const x of xs || []) {
        const v = _extractAxisLabel(x, kind);
        if (!v) continue;
        if (seen.has(v)) continue;
        seen.add(v);
        out.push(v);
    }
    return out;
};

const _maxImpactLevel = (impacts) => {
    if (!Array.isArray(impacts) || impacts.length === 0) return ORG_LEVEL_FALLBACK;
    let bestRank = -1;
    let bestLevel = ORG_LEVEL_FALLBACK;
    for (const imp of impacts) {
        const lvl = (imp?.impactLevel || "").toUpperCase();
        if (!lvl || lvl === ORG_LEVEL_FALLBACK) continue;
        const r = levelRank(lvl);
        if (r > bestRank) {
            bestRank = r;
            bestLevel = lvl;
        }
    }
    return bestLevel;
};

/** Read the configured axes from the client profile. Honours the verbatim
 *  `clientProfile.businessSegments` / `clientProfile.capabilityAreas` arrays
 *  when present, otherwise infers from `matrixConfig.rows` / `.columns` (whose
 *  names are misleading — `rows` historically held BUSINESS SEGMENTS and
 *  `columns` held CAPABILITY AREAS in the upstream schema). */
export const readClientMatrixAxes = (clientProfile) => {
    const cp = clientProfile || {};
    const fromVerbatimBs = _axisStrings(cp.businessSegments, "businessSegment");
    const fromConfigBs = _axisStrings((cp.matrixConfig && cp.matrixConfig.rows) || [], "businessSegment");
    const businessSegments = fromVerbatimBs.length ? fromVerbatimBs : fromConfigBs;

    const fromVerbatimCa = _axisStrings(cp.capabilityAreas, "capabilityArea");
    const fromConfigCa = _axisStrings((cp.matrixConfig && cp.matrixConfig.columns) || [], "capabilityArea");
    const capabilityAreas = fromVerbatimCa.length ? fromVerbatimCa : fromConfigCa;

    return { businessSegments, capabilityAreas };
};

/** Build the full Organizational Exposure matrix for the selected client.
 *
 *  @param {Object} args
 *  @param {Array|undefined} args.exposureMatrix - the upstream
 *      `organizationalExposureMatrix` (list of `{ businessSegment, cells: [
 *      { businessSegment, capabilityArea, impactLevel, impactCount, impacts }
 *      ] }`).
 *  @param {Object|undefined} args.clientProfile - the client's profile with
 *      `businessSegments`, `capabilityAreas`, and `matrixConfig`.
 *  @param {Array|undefined} args.impactCellAssessments - the flat top-level
 *      `impactCellAssessments` array (per `client-signal-view-v2.1-matrix-cells`),
 *      used only as a fallback when `exposureMatrix` is missing. The legacy
 *      `impactMatrix` alias is no longer read.
 *  @param {string} args.scope - `"all"` or `"selected"`.
 *  @param {string|null} args.selectedTrackId - track id to filter impacts by
 *      when scope is `"selected"`.
 *  @returns {{
 *      businessSegments: string[],
 *      capabilityAreas: string[],
 *      cells: Object<string, { businessSegment, capabilityArea, impactLevel,
 *          impactCount, impactType, impacts: any[] }>,
 *      count: number,
 *      source: "exposureMatrix" | "fallback-flat" | "axes-only"
 *  }}
 *
 *  Cell key shape: `"${capabilityArea}__${businessSegment}"`.
 */
export const buildOrganizationalExposureMatrix = ({
    exposureMatrix,
    clientProfile,
    impactCellAssessments,
    scope = "all",
    selectedTrackId = null,
} = {}) => {
    // 1. Axes (rows = capability areas, columns = business segments) come
    //    from the client profile and are ALWAYS shown in their configured
    //    order. Empty axes are tolerated but render as an empty grid.
    const { businessSegments: profileBS, capabilityAreas: profileCA } = readClientMatrixAxes(clientProfile);

    // 2. Build a flat list of impacted cells from whichever source is present.
    //    Each entry: { businessSegment, capabilityArea, impactLevel, impactCount, impacts[] }.
    let source = "axes-only";
    const flatCells = [];

    if (Array.isArray(exposureMatrix) && exposureMatrix.length > 0) {
        source = "exposureMatrix";
        for (const row of exposureMatrix) {
            if (!row || typeof row !== "object") continue;
            const bs = (row.businessSegment || "").trim();
            const cells = Array.isArray(row.cells) ? row.cells : [];
            for (const cell of cells) {
                if (!cell || typeof cell !== "object") continue;
                const cellBs = (cell.businessSegment || bs || "").trim();
                const cellCa = (cell.capabilityArea || "").trim();
                if (!cellBs || !cellCa) continue;
                flatCells.push({
                    businessSegment: cellBs,
                    capabilityArea: cellCa,
                    impactLevel: (cell.impactLevel || "").toUpperCase(),
                    impactCount: typeof cell.impactCount === "number" ? cell.impactCount : null,
                    impactType: cell.impactType || null,
                    impacts: Array.isArray(cell.impacts) ? cell.impacts : [],
                });
            }
        }
    } else if (Array.isArray(impactCellAssessments) && impactCellAssessments.length > 0) {
        source = "fallback-flat";
        // Group cell assessments by (bs, ca). Each row is an impact entry.
        const grouped = new Map();
        for (const r of impactCellAssessments) {
            if (!r || typeof r !== "object") continue;
            const bs = (r.businessSegment || "").trim();
            const ca = (r.capabilityArea || "").trim();
            if (!bs || !ca) continue;
            const k = `${bs}__${ca}`;
            if (!grouped.has(k)) {
                grouped.set(k, {
                    businessSegment: bs,
                    capabilityArea: ca,
                    impactType: r.impactType || null,
                    impacts: [],
                });
            }
            grouped.get(k).impacts.push(r);
        }
        for (const c of grouped.values()) {
            flatCells.push({
                ...c,
                impactLevel: _maxImpactLevel(c.impacts),
                impactCount: c.impacts.length,
            });
        }
    }

    // 3. Add any cell-level axes that the profile didn't enumerate, so cells
    //    aren't silently dropped if the upstream matrix references an axis
    //    label the profile forgot. The profile order is preserved; the extras
    //    are appended in first-seen order.
    const bsSet = new Set(profileBS);
    const caSet = new Set(profileCA);
    const extraBs = [];
    const extraCa = [];
    for (const c of flatCells) {
        if (c.businessSegment && !bsSet.has(c.businessSegment) && !extraBs.includes(c.businessSegment)) {
            extraBs.push(c.businessSegment);
        }
        if (c.capabilityArea && !caSet.has(c.capabilityArea) && !extraCa.includes(c.capabilityArea)) {
            extraCa.push(c.capabilityArea);
        }
    }
    const businessSegments = [...profileBS, ...extraBs];
    const capabilityAreas = [...profileCA, ...extraCa];

    // 4. Populate the cells map. EVERY (ca, bs) combo gets an entry; missing
    //    combos render as NO_IMPACT and are still part of the grid.
    const cells = {};
    for (const ca of capabilityAreas) {
        for (const bs of businessSegments) {
            const key = `${ca}__${bs}`;
            cells[key] = {
                businessSegment: bs,
                capabilityArea: ca,
                impactLevel: ORG_LEVEL_FALLBACK,
                impactCount: 0,
                impactType: null,
                impacts: [],
            };
        }
    }

    // 5. Overlay impacted cells. Re-derive impactLevel/Count when scope
    //    actively filters impacts (selected mode + selectedTrackId). In "all"
    //    mode we trust the upstream cell as-is, including cases where the
    //    upstream `impacts[]` is empty but `impactLevel` is set.
    let totalImpacts = 0;
    const filtering = scope === "selected" && !!selectedTrackId;
    for (const c of flatCells) {
        const key = `${c.capabilityArea}__${c.businessSegment}`;
        if (!cells[key]) continue; // out-of-axis cell (defensively skipped)

        if (!filtering) {
            // Trust the upstream cell verbatim. NO_IMPACT cells stay as the
            // initialised default.
            const lvl = (c.impactLevel || "").toUpperCase();
            if (lvl && lvl !== ORG_LEVEL_FALLBACK) {
                cells[key] = {
                    businessSegment: c.businessSegment,
                    capabilityArea: c.capabilityArea,
                    impactLevel: lvl,
                    impactCount: typeof c.impactCount === "number" ? c.impactCount : (c.impacts ? c.impacts.length : 0),
                    impactType: c.impactType,
                    impacts: c.impacts,
                };
                totalImpacts += (typeof c.impactCount === "number" ? c.impactCount : (c.impacts ? c.impacts.length : 0));
            }
            continue;
        }

        // Filtering by selected track: re-derive level + count from the
        // subset of impacts that match the selected trackId.
        const filteredImpacts = (c.impacts || []).filter((imp) => imp && imp.trackId === selectedTrackId);
        if (filteredImpacts.length === 0) continue;

        cells[key] = {
            businessSegment: c.businessSegment,
            capabilityArea: c.capabilityArea,
            impactLevel: _maxImpactLevel(filteredImpacts) || ORG_LEVEL_FALLBACK,
            impactCount: filteredImpacts.length,
            impactType: c.impactType,
            impacts: filteredImpacts,
        };
        totalImpacts += filteredImpacts.length;
    }

    return {
        businessSegments,
        capabilityAreas,
        cells,
        count: totalImpacts,
        source,
    };
};

const PRIORITY_ORDER = ["HIGH", "MEDIUM", "LOW"];
const PRIORITY_RANK = PRIORITY_ORDER.reduce((acc, p, i) => { acc[p] = i; return acc; }, {});

/** Group action backlog rows by priority, preserving order HIGH→MEDIUM→LOW. */
export const groupActionsByPriority = (actions) => {
    if (!Array.isArray(actions)) return [];
    const groups = new Map();
    for (const a of actions) {
        const p = (a?.priority || "MEDIUM").toUpperCase();
        if (!groups.has(p)) groups.set(p, []);
        groups.get(p).push(a);
    }
    for (const list of groups.values()) {
        list.sort((a, b) => {
            const ph = (a?.timeHorizon || "").localeCompare(b?.timeHorizon || "");
            if (ph !== 0) return ph;
            return String(a?.actionTitle || "").localeCompare(String(b?.actionTitle || ""));
        });
    }
    return [...groups.entries()]
        .sort((a, b) => (PRIORITY_RANK[a[0]] ?? 99) - (PRIORITY_RANK[b[0]] ?? 99))
        .map(([priority, actions]) => ({ priority, actions }));
};

/** Pull every trackId out of an action row, regardless of whether the upstream
 *  feed spells it `relatedTrackIds` / `relatedTrackIDs` / `RelatedTrackIDs`. */
export const relatedTrackIdsOf = (action) => {
    if (!action || typeof action !== "object") return [];
    const merged = [];
    for (const key of ["relatedTrackIds", "relatedTrackIDs", "RelatedTrackIDs", "RelatedTrackIds"]) {
        const v = action[key];
        if (Array.isArray(v)) merged.push(...v);
    }
    return [...new Set(merged.map((x) => String(x || "").trim()).filter(Boolean))];
};

/** Normalise a weekly-lens narrative entry (opportunity / threat / blind spot)
 *  to a lowerCamelCase shape so the UI doesn't care whether the upstream feed
 *  authored fields as `Title` / `Summary` / `Type` (PascalCase, as LBBW W23
 *  W23 ships them) or `title` / `summary` / `category` (legacy lowerCamel).
 *  Returns the input untouched when it isn't a plain object. */
export const normaliseNarrativeEntry = (entry) => {
    if (!entry || typeof entry !== "object") return entry;
    const out = { ...entry };
    const pick = (keys) => {
        for (const k of keys) {
            const v = entry[k];
            if (v !== undefined && v !== null && v !== "") return v;
        }
        return undefined;
    };
    const title = pick(["title", "Title"]);
    const summary = pick(["summary", "Summary"]);
    const whyItMatters = pick(["whyItMatters", "WhyItMatters", "why_it_matters"]);
    const category = pick(["category", "Category", "type", "Type"]);
    const severity = pick(["severity", "Severity", "priority", "Priority"]);
    const recommendedAction = pick(["recommendedAction", "RecommendedAction", "suggestedStep", "SuggestedStep"]);
    const clientRelevance = pick(["clientRelevance", "ClientRelevance"]);
    if (title !== undefined) out.title = title;
    if (summary !== undefined) out.summary = summary;
    if (whyItMatters !== undefined) out.whyItMatters = whyItMatters;
    if (category !== undefined) out.category = category;
    if (severity !== undefined) out.severity = severity;
    if (recommendedAction !== undefined) out.recommendedAction = recommendedAction;
    if (clientRelevance !== undefined) out.clientRelevance = clientRelevance;
    return out;
};

/** Actions that should appear for a given trackId. Prefers
 *  `selectedTrack.relatedActions` when present, otherwise filters the
 *  page-level `actionBacklog` by trackId. */
export const actionsForTrack = ({ selectedTrack, actionBacklog }) => {
    if (selectedTrack && Array.isArray(selectedTrack.relatedActions) && selectedTrack.relatedActions.length > 0) {
        return selectedTrack.relatedActions;
    }
    if (!Array.isArray(actionBacklog) || actionBacklog.length === 0 || !selectedTrack) return [];
    const id = selectedTrack.trackId;
    if (!id) return [];
    return actionBacklog.filter((a) => relatedTrackIdsOf(a).includes(id));
};

/** Sort actionBacklog rows by priority → time horizon → title. */
export const sortBacklog = (actions) => {
    if (!Array.isArray(actions)) return [];
    return [...actions].sort((a, b) => {
        const ra = (PRIORITY_RANK[(a?.priority || "MEDIUM").toUpperCase()] ?? 99);
        const rb = (PRIORITY_RANK[(b?.priority || "MEDIUM").toUpperCase()] ?? 99);
        if (ra !== rb) return ra - rb;
        const dh = String(a?.timeHorizon || "").localeCompare(String(b?.timeHorizon || ""));
        if (dh !== 0) return dh;
        return String(a?.actionTitle || a?.action || "").localeCompare(String(b?.actionTitle || b?.action || ""));
    });
};

/** Normalise an entry from `weeklyLens.topRecommendedActions` into the same
 *  shape as a backlog action so the UI can render either source uniformly. */
export const normaliseRecommendedAction = (entry) => {
    if (!entry || typeof entry !== "object") return null;
    return {
        actionId: entry.actionId || null,
        actionTitle: entry.actionTitle || entry.action || null,
        actionType: entry.actionType || null,
        priority: entry.priority || null,
        timeHorizon: entry.timeHorizon || entry.timeframe || null,
        ownerSuggestion: entry.ownerSuggestion || entry.owner || null,
        rationale: entry.rationale || entry.whyItMatters || null,
        firstStep: entry.firstStep || null,
        successMetric: entry.successMetric || null,
        budgetIndication: entry.budgetIndication || null,
        affectedBusinessSegments: entry.affectedBusinessSegments || [],
        affectedCapabilityAreas: entry.affectedCapabilityAreas || [],
        relatedTrackIds: relatedTrackIdsOf(entry),
        relatedTrackNames: entry.relatedTrackNames || [],
        rank: typeof entry.rank === "number" ? entry.rank : null,
    };
};

/** Best-effort extraction of evidence URLs from a track card's `evidence`
 *  block. Returns [{ url, label, host }]. */
export const extractEvidenceUrls = (evidence) => {
    if (!evidence || typeof evidence !== "object") return [];
    const raw = evidence.urls || evidence.URLs || evidence.evidenceUrls;
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const item of raw) {
        if (!item) continue;
        if (typeof item === "string") {
            const host = hostnameOf(item);
            out.push({ url: item, label: host, host });
        } else if (typeof item === "object") {
            const url = item.url || item.URL || item.link;
            if (!url) continue;
            const host = hostnameOf(url);
            out.push({ url, label: item.title || item.label || host, host });
        }
    }
    return out;
};

const hostnameOf = (u) => {
    try {
        return new URL(u).hostname.replace(/^www\./, "");
    } catch {
        return u;
    }
};

/** Friendly relative-time string for ISO timestamps. */
export const relativeFromIso = (iso) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h ago`;
    const d = Math.floor(h / 24);
    return `${d} d ago`;
};

/** Returns true when the value has at least one user-meaningful character /
 *  item. Treats empty strings, empty arrays, null, undefined as missing. */
export const hasContent = (v) => {
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim().length > 0;
    return true;
};

// ---------------------------------------------------------------------------
// Blind spots — split into profile-level (capability-wide gaps) and
// signal-linked (gaps tied to one market signal), deduped by dedupeKey.
// ---------------------------------------------------------------------------

export const SEVERITY_TONE = {
    HIGH: "negative",
    MEDIUM: "warning",
    LOW: "muted",
};

const dedupeBy = (items, key) => {
    const out = [];
    const seen = new Set();
    for (const it of items || []) {
        if (!it || typeof it !== "object") continue;
        const k = it[key];
        if (k) {
            if (seen.has(k)) continue;
            seen.add(k);
        }
        out.push(it);
    }
    return out;
};

/** Build the two-group blind-spot view for the weekly read or the selected
 *  track. Falls back to legacy `topBlindSpots` when the structured fields
 *  aren't published yet. */
export const buildBlindSpotGroups = ({
    profileLevelBlindSpots,
    signalLinkedBlindSpots,
    structuredBlindSpots,
    legacyTopBlindSpots,
    blindSpots,
} = {}) => {
    const profile = dedupeBy(
        (Array.isArray(profileLevelBlindSpots) && profileLevelBlindSpots) ||
        (Array.isArray(structuredBlindSpots) ? structuredBlindSpots.filter((b) => (b?.blindSpotType || "").toUpperCase() === "PROFILE_LEVEL") : null) ||
        [],
        "dedupeKey"
    );
    const signalLinked = dedupeBy(
        (Array.isArray(signalLinkedBlindSpots) && signalLinkedBlindSpots) ||
        (Array.isArray(structuredBlindSpots) ? structuredBlindSpots.filter((b) => (b?.blindSpotType || "").toUpperCase() !== "PROFILE_LEVEL") : null) ||
        (Array.isArray(blindSpots) ? blindSpots : null) ||
        [],
        "dedupeKey"
    );
    // If we have nothing structured, surface the legacy fallback in
    // signalLinked so the UI still shows something useful.
    if (profile.length === 0 && signalLinked.length === 0 && Array.isArray(legacyTopBlindSpots)) {
        return { profile: [], signalLinked: dedupeBy(legacyTopBlindSpots, "dedupeKey"), usedLegacy: true };
    }
    return { profile, signalLinked, usedLegacy: false };
};

export const ENGAGEMENT_MODEL_LABEL = {
    VENDOR_EVALUATION: "Vendor evaluation",
    BENCHMARK_ONLY: "Benchmark only",
    CONSORTIUM_PEER: "Consortium peer",
    TECHNICAL_PARTNER: "Technical partner",
    DISTRIBUTION_PARTNER: "Distribution partner",
    MONITOR_ONLY: "Monitor only",
};

export const engagementLabel = (model) => {
    const u = (model || "").toUpperCase();
    return ENGAGEMENT_MODEL_LABEL[u] || (model ? model.toString().replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase()) : null);
};

/** Read the new market-signal-context shape (or legacy fallbacks) off a track
 *  card and return a single normalised object. */
export const readMarketSignalContext = (card) => {
    if (!card) return null;
    const c = card.marketSignalContext || card.market?.signalContext || card.interpretation?.marketSignalContext || {};
    const m = card.market || {};
    const i = card.interpretation || {};
    return {
        trackThesis: c.TrackThesis || c.trackThesis || card.trackThesis || m.trackThesis || null,
        primaryUseCase: c.PrimaryUseCase || c.primaryUseCase || card.primaryUseCase || m.primaryUseCase || null,
        primaryMechanism: c.PrimaryMechanism || c.primaryMechanism || card.primaryMechanism || m.primaryMechanism || null,
        macroTheme: c.MacroTheme || c.macroTheme || card.macroTheme || m.macroTheme || null,
        latestWeeklyFraming: c.LatestWeeklyFraming || c.latestWeeklyFraming || card.latestWeeklyFraming || null,
        currentMarketMovement: c.CurrentMarketMovement || c.currentMarketMovement || m.currentMarketMovement || i.currentMarketMovement || null,
        whyRelevantToClient: c.WhyRelevantToClient || c.whyRelevantToClient || i.whyRelevantToClient || card?.client?.whyRelevantToClient || null,
    };
};

/** Normalise client-positioning data — handles top-level + nested locations. */
export const readClientPositioning = (card) => {
    if (!card) return null;
    const p = card.clientPositioning || card.client?.positioning || card.interpretation?.clientPositioning || {};
    const c = card.client || {};
    const positioning = (typeof p === "string" ? p : null) || p.positioning || p.Positioning || c.positioning || c.position || null;
    return {
        positioning,
        positioningRationale: p.positioningRationale || c.positioningRationale || null,
        confirmedClientActivities: p.confirmedClientActivities || c.confirmedClientActivities || null,
        existingStrengths: p.existingStrengths || c.existingStrengths || null,
        gaps: p.gaps || c.gaps || null,
        positioningRecommendedNextAction:
            p.positioningRecommendedNextAction
            || c.positioningRecommendedNextAction
            || c.recommendedNextAction
            || card?.interpretation?.recommendedNextAction
            || null,
    };
};

// ---------------------------------------------------------------------------
// Client-relevance explanation read.
// Aggregates the new track-level relevance explanation fields from
// trackCard.client / trackCard / trackCard.interpretation, applying the
// preferred-access-path fallbacks from the upstream contract:
//   - relevanceFrontendSummary:   client → root → interpretation → whyRelevantToClient
//   - rationale:                  client → root → interpretation
//   - selectionRationale:         client → root → interpretation
//   - relevanceExplanation:       client → root → interpretation
//   - relevanceScoreDrivers:      client → interpretation.clientRelevanceScoreDrivers
//   - topPositiveDrivers/penalties: client.*
//   - deterministicCandidateScore / scorePenalty: client.*
//   - weeklyDynamicsMatchType, hasCurrentDynamics, candidateSelectionMode: client.*
// All fields default to null/empty array so callers can hide UI gracefully.
// ---------------------------------------------------------------------------

const _firstString = (...xs) => {
    for (const x of xs) {
        if (typeof x === "string" && x.trim()) return x.trim();
    }
    return null;
};

const _firstArray = (...xs) => {
    for (const x of xs) {
        if (Array.isArray(x) && x.length) return x;
    }
    return [];
};

const _firstObject = (...xs) => {
    for (const x of xs) {
        if (x && typeof x === "object" && !Array.isArray(x)) return x;
    }
    return null;
};

/** Returns a normalised view of all client-relevance explanation fields for
 *  a single track card. Always returns an object; every field defaults to
 *  null / empty array so the UI can hide blocks gracefully. */
export const readClientRelevance = (card) => {
    const c = card?.client || {};
    const root = card || {};
    const i = card?.interpretation || {};

    // Pre-split topPositiveDrivers / relevancePenalties (when authored
    // explicitly). Otherwise we derive them later from relevanceScoreDrivers.
    const explicitPositive = Array.isArray(c.topPositiveDrivers) ? c.topPositiveDrivers : [];
    const explicitPenalties = Array.isArray(c.relevancePenalties) ? c.relevancePenalties : [];

    const drivers = _firstArray(
        c.relevanceScoreDrivers,
        i.clientRelevanceScoreDrivers,
        root.relevanceScoreDrivers,
    );

    // If explicit split isn't provided, derive it from `drivers` by sign of
    // `impact`. Treat impact === 0 as positive (no penalty).
    const positiveDrivers = explicitPositive.length
        ? explicitPositive
        : drivers.filter((d) => {
            if (!d || typeof d !== "object") return false;
            if (typeof d.direction === "string") return d.direction.toUpperCase() === "POSITIVE";
            return (typeof d.impact !== "number") || d.impact >= 0;
        });
    const penalties = explicitPenalties.length
        ? explicitPenalties
        : drivers.filter((d) => {
            if (!d || typeof d !== "object") return false;
            if (typeof d.direction === "string") return d.direction.toUpperCase() === "NEGATIVE";
            return typeof d.impact === "number" && d.impact < 0;
        });

    return {
        score: typeof c.relevanceScore === "number" ? c.relevanceScore : null,
        level: typeof c.relevanceLevel === "string" ? c.relevanceLevel : null,
        position: typeof c.position === "string" ? c.position : null,
        cardCategory: typeof c.cardCategory === "string" ? c.cardCategory : null,
        weeklyDynamicsMatchType: typeof c.weeklyDynamicsMatchType === "string" ? c.weeklyDynamicsMatchType : null,
        hasCurrentDynamics: typeof c.hasCurrentDynamics === "boolean" ? c.hasCurrentDynamics : null,
        candidateSelectionMode: typeof c.candidateSelectionMode === "string" ? c.candidateSelectionMode : null,
        deterministicCandidateScore: typeof c.deterministicCandidateScore === "number" ? c.deterministicCandidateScore : null,
        scorePenalty: typeof c.scorePenalty === "number" ? c.scorePenalty : null,

        relevanceFrontendSummary: _firstString(
            c.relevanceFrontendSummary,
            root.relevanceFrontendSummary,
            i.relevanceFrontendSummary,
            i.whyRelevantToClient,
            c.whyRelevantToClient,
        ),
        clientRelevanceRationale: _firstString(
            c.clientRelevanceRationale,
            c.relevanceRationale,
            root.clientRelevanceRationale,
            i.clientRelevanceRationale,
        ),
        selectionRationale: _firstString(
            c.selectionRationale,
            root.selectionRationale,
            i.selectionRationale,
        ),
        relevanceExplanation: _firstObject(
            c.relevanceExplanation,
            root.relevanceExplanation,
            i.relevanceExplanation,
        ),

        drivers,
        positiveDrivers,
        penalties,

        clientFitEvidence: Array.isArray(c.clientFitEvidence) ? c.clientFitEvidence : [],
    };
};

// ---------------------------------------------------------------------------
// Humanised display labels for technical enum values surfaced in Client View.
// Internal data values are NOT renamed — only UI display strings.
// ---------------------------------------------------------------------------

/** `candidateSelectionMode` enum → reader-friendly label. */
export const SELECTION_MODE_LABEL = {
    PRIMARY_CARD: "Primary signal",
    SUPPORTING_SUB_PATTERN: "Supporting pattern",
    EXPANSION_CARD: "Expansion signal",
    SECONDARY_CARD: "Secondary signal",
    SUPPORTING_CARD: "Supporting signal",
    EXPLORATORY_CARD: "Exploratory signal",
    BACKFILL: "Backfill",
};

/** `weeklyDynamicsMatchType` enum → reader-friendly label. */
export const MATCH_TYPE_LABEL = {
    EXACT: "Exact",
    STRONG: "Strong",
    PARTIAL: "Partial",
    WEAK: "Weak",
    NONE: "None",
    NO_MATCH: "No match",
    INDIRECT: "Indirect",
};

/** Track status enum (Client View) → reader-friendly label. Aligned with the
 *  Market View status terminology but shorter for the Client View context.
 *  Keys are accepted in multiple shapes: snake_case (`CORE_TRACK`),
 *  space-separated (`Core Track`, `WATCHLIST TRACK`), and pure single tokens. */
export const SIGNAL_STATUS_LABEL = {
    CORE_TRACK: "Core signal",
    CORE_SIGNAL: "Core signal",
    CORE: "Core signal",
    "CORE TRACK": "Core signal",
    WATCHLIST_TRACK: "Watchlist signal",
    WATCHLIST_SIGNAL: "Watchlist signal",
    "WATCHLIST TRACK": "Watchlist signal",
    WATCHLIST: "Watchlist signal",
    SUB_PATTERN: "Supporting pattern",
    "SUB PATTERN": "Supporting pattern",
    SUBPATTERN: "Supporting pattern",
    WEEKLY_DYNAMIC: "This week's movement",
    WEEKLY_DYNAMICS: "This week's movement",
    WEEKLY_MOVEMENT: "This week's movement",
    "WEEKLY MOVEMENT": "This week's movement",
    UNCURATED: "Uncurated",
    EXPLORATORY: "Uncurated",
    EXPLORATORY_CLUSTER: "Uncurated",
    "EXPLORATORY CLUSTER": "Uncurated",
    NOISE: "Uncurated",
};

/** `ClientPositioningCategory` enum → reader-friendly label. Drives the
 *  per-impact pills in the matrix-cell detail panel. Spec: ADVANTAGE,
 *  OPPORTUNITY, THREAT, MONITOR, NO_IMPACT (client-specific overlay,
 *  distinct from structural `ImpactLevel`). */
export const CLIENT_POSITIONING_LABEL = {
    ADVANTAGE: "Advantage",
    OPPORTUNITY: "Opportunity",
    THREAT: "Threat",
    MONITOR: "Monitor",
    MONITOR_ONLY: "Monitor",
    NO_IMPACT: "No material impact",
    EMERGING_FIT: "Emerging fit",
    IN_POSITION: "In position",
};

/** `ClientPositioningCategory` → semantic tone for the badge tint. */
export const CLIENT_POSITIONING_TONE = {
    ADVANTAGE: "positive",
    OPPORTUNITY: "accent",
    THREAT: "negative",
    MONITOR: "muted",
    MONITOR_ONLY: "muted",
    NO_IMPACT: "muted",
    EMERGING_FIT: "warning",
    IN_POSITION: "positive",
};

export const clientPositioningLabel = (value) => {
    if (value == null) return null;
    const u = String(value).trim().toUpperCase();
    if (!u) return null;
    return CLIENT_POSITIONING_LABEL[u] || _humanizeFallback(value);
};

/** Weekly dynamics enum → reader-friendly label. */
export const WEEKLY_DYNAMICS_LABEL = {
    NO_CURRENT_DYNAMIC: "No current movement",
    NO_CURRENT_DYNAMICS: "No current movement",
    CURRENT_DYNAMIC: "Active weekly movement",
    CURRENT_DYNAMICS: "Active weekly movement",
    HISTORICAL_ONLY: "Historical only",
    STRENGTHENING: "Strengthening",
    GAINING: "Strengthening",
    SOFTENING: "Softening",
    WEAKENING: "Softening",
    FADING: "Softening",
    STABLE: "Stable",
    HOLDING_STEADY: "Stable",
    HOLDING: "Stable",
    MIXED: "Mixed momentum",
    BROADENING: "Broadening",
    NEW: "New evidence",
    NEW_EVIDENCE: "New evidence",
};

/** Tone hint for the weekly-dynamics / momentum badge. */
export const WEEKLY_DYNAMICS_TONE = {
    STRENGTHENING: "positive",
    GAINING: "positive",
    BROADENING: "accent",
    NEW: "accent",
    NEW_EVIDENCE: "accent",
    SOFTENING: "negative",
    WEAKENING: "negative",
    FADING: "negative",
    STABLE: "muted",
    HOLDING_STEADY: "muted",
    HOLDING: "muted",
    MIXED: "muted",
    NO_CURRENT_DYNAMIC: "muted",
    NO_CURRENT_DYNAMICS: "muted",
    HISTORICAL_ONLY: "muted",
};

/** Convenience: humanise a market-momentum value (or null). */
export const marketMomentumLabel = (value) => {
    if (value == null) return null;
    const u = String(value).trim().toUpperCase();
    return WEEKLY_DYNAMICS_LABEL[u] || _humanizeFallback(value);
};
export const marketMomentumTone = (value) => {
    if (value == null) return "muted";
    const u = String(value).trim().toUpperCase();
    return WEEKLY_DYNAMICS_TONE[u] || "muted";
};

const _humanizeFallback = (raw) => {
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!s) return null;
    return s.replace(/_/g, " ").toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
};

/** Generic enum → human label resolver. Tries `kind`-specific map first, then
 *  falls back to a snake-case → "Title case" transform. */
export const humanizeEnumLabel = (value, kind) => {
    if (value == null) return null;
    const u = String(value).trim().toUpperCase();
    if (!u) return null;
    if (kind === "selectionMode" && SELECTION_MODE_LABEL[u]) return SELECTION_MODE_LABEL[u];
    if (kind === "matchType" && MATCH_TYPE_LABEL[u]) return MATCH_TYPE_LABEL[u];
    if (kind === "signalStatus" && SIGNAL_STATUS_LABEL[u]) return SIGNAL_STATUS_LABEL[u];
    if (kind === "level" && LEVEL_RANK[u]) return levelLabel(value);
    if (kind === "clientPositioning" && CLIENT_POSITIONING_LABEL[u]) return CLIENT_POSITIONING_LABEL[u];
    if (kind === "weeklyDynamics" && WEEKLY_DYNAMICS_LABEL[u]) return WEEKLY_DYNAMICS_LABEL[u];
    // Cross-kind safety net — `CLIENT_POSITIONING_LABEL` covers most enum
    // values surfaced today (NO_IMPACT, EMERGING_FIT, IN_POSITION,
    // MONITOR_ONLY) and can also be used when the caller passes no kind.
    if (CLIENT_POSITIONING_LABEL[u]) return CLIENT_POSITIONING_LABEL[u];
    if (WEEKLY_DYNAMICS_LABEL[u]) return WEEKLY_DYNAMICS_LABEL[u];
    return _humanizeFallback(value);
};

/** Reader-friendly Client View status label. For values matching a known
 *  status enum (CORE_TRACK / WATCHLIST_TRACK / SUB_PATTERN / UNCURATED /
 *  EXPLORATORY / EXPLORATORY_CLUSTER / NOISE), returns the mapped Client View
 *  label. For values that aren't recognised, returns the raw input verbatim
 *  (since upstream data may already be human-formatted, e.g. "Structural Trend").
 *  Returns null for empty/null input. */
export const signalStatusLabel = (raw) => {
    if (raw == null) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    const u = trimmed.toUpperCase();
    if (SIGNAL_STATUS_LABEL[u]) return SIGNAL_STATUS_LABEL[u];
    const norm = u.replace(/[_\-]+/g, " ").replace(/\s+/g, " ").trim();
    if (SIGNAL_STATUS_LABEL[norm]) return SIGNAL_STATUS_LABEL[norm];
    const underscored = norm.replace(/\s+/g, "_");
    if (SIGNAL_STATUS_LABEL[underscored]) return SIGNAL_STATUS_LABEL[underscored];
    // Pass through the original (already-formatted) value untouched.
    return trimmed;
};

/** Reader-friendly Client View status tone. */
export const signalStatusTone = (raw) => {
    const u = String(raw || "").trim().toUpperCase().replace(/[_\-]+/g, " ").replace(/\s+/g, " ").trim();
    if (u === "CORE TRACK" || u === "CORE SIGNAL") return "ink";
    if (u === "WATCHLIST TRACK" || u === "WATCHLIST SIGNAL" || u === "WATCHLIST") return "warning";
    if (u === "SUB PATTERN" || u === "SUBPATTERN") return "muted";
    return "muted";
};

// ---------------------------------------------------------------------------
// Relevance summary helpers — board shortening + drawer de-duplication.
// ---------------------------------------------------------------------------

/** Normalise a string for similarity comparison: lowercase, strip punctuation
 *  and quotes, collapse whitespace. Returns "" when input is empty. */
const _normalizeForCompare = (s) => {
    if (s == null) return "";
    return String(s)
        .toLowerCase()
        .replace(/[\u2018\u2019\u201C\u201D"'`]/g, "")
        .replace(/[\u2013\u2014\-_/\\.,;:!?()\[\]{}]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

/** Tokenize for set-overlap comparison. Drops stopwords + short tokens. */
const _STOPWORDS = new Set([
    "the", "a", "an", "and", "or", "but", "of", "to", "for", "in", "on", "at",
    "by", "with", "from", "as", "is", "are", "was", "were", "be", "been",
    "being", "this", "that", "these", "those", "it", "its", "their", "they",
    "his", "her", "our", "your", "you", "we", "us", "i", "me",
]);
const _tokenSet = (s) => {
    const norm = _normalizeForCompare(s);
    if (!norm) return new Set();
    const out = new Set();
    for (const tok of norm.split(" ")) {
        if (tok.length < 3) continue;
        if (_STOPWORDS.has(tok)) continue;
        out.add(tok);
    }
    return out;
};

/** True when `rationale` essentially repeats `summary` (exact normalised
 *  match, summary contains rationale, rationale contains summary, or Jaccard
 *  overlap ≥ 0.8 on meaningful tokens). Empty inputs are NOT treated as duplicate. */
export const isDuplicateRelevanceText = (summary, rationale) => {
    if (!hasContent(summary) || !hasContent(rationale)) return false;
    const a = _normalizeForCompare(summary);
    const b = _normalizeForCompare(rationale);
    if (!a || !b) return false;
    if (a === b) return true;
    // Containment check — one fully contains the other (with minor padding)
    if (a.length >= b.length && a.includes(b)) return true;
    if (b.length >= a.length && b.includes(a)) return true;
    // Token-set Jaccard overlap on meaningful tokens
    const ta = _tokenSet(summary);
    const tb = _tokenSet(rationale);
    if (ta.size === 0 || tb.size === 0) return false;
    let intersection = 0;
    for (const t of ta) if (tb.has(t)) intersection += 1;
    const union = ta.size + tb.size - intersection;
    if (union === 0) return false;
    const jaccard = intersection / union;
    return jaccard >= 0.8;
};

/** Strip a leading "<Signal Name> — " / ": " / "- " / "– " prefix (or the
 *  common "<Signal Name> has high relevance for <client>: " style) from the
 *  relevance summary so the board row shows only the explanatory tail.
 *  Falls back to the original summary when there's no clean prefix match. */
export const stripSignalNamePrefix = (summary, signalName) => {
    if (!hasContent(summary)) return summary || "";
    const s = String(summary).trim();
    if (!hasContent(signalName)) return s;
    const name = String(signalName).trim();
    if (!name) return s;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // 1. Direct separator: "<Name> — " / ": " / " - " etc
    const direct = new RegExp(`^${escaped}\\s*(?:[\\u2014\\u2013:\\-]|—|–)\\s+`, "i");
    let trimmed = s.replace(direct, "").trim();
    if (trimmed !== s) {
        if (!trimmed || trimmed.length < 3) return s;
        return trimmed;
    }
    // 2. "<Name> <verb phrase>: <rest>" — strip when summary STARTS with the
    //    signal name and there's a colon within the first ~140 chars and the
    //    remaining text is substantial (≥ 20 chars). Catches phrasings like
    //    "<Name> has high relevance for <Client>: …".
    const startsWithName = new RegExp(`^${escaped}\\s`, "i");
    if (startsWithName.test(s)) {
        const colonIdx = s.indexOf(":");
        if (colonIdx > 0 && colonIdx <= 140) {
            const tail = s.slice(colonIdx + 1).trim();
            if (tail.length >= 20) return tail;
        }
    }
    return s;
};

/** Read `weeklyLens.whatMovedThisWeek` items defensively. Always returns an array. */
/** Read `weeklyLens.whatMovedThisWeek` items defensively. Always returns an
 *  array of normalised entries (PascalCase ↔ lowerCamel) and drops entries
 *  that would render to nothing (no title AND no summary post-normalisation).
 *  Upstream entries may use either PascalCase keys (`Title` / `Summary` /
 *  `ClientRelevance` / `RelatedTrackIDs`) as authored in the current LBBW
 *  feed, or lowerCamel keys in older feeds — both shapes are supported. */
export const readWhatMovedThisWeek = (weeklyLens) => {
    if (!weeklyLens || typeof weeklyLens !== "object") return [];
    const raw = weeklyLens.whatMovedThisWeek;
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const item of raw) {
        const e = normaliseNarrativeEntry(item);
        if (!e || typeof e !== "object") continue;
        if (!hasContent(e.title) && !hasContent(e.summary)) continue;
        out.push(e);
    }
    return out;
};

// ---------------------------------------------------------------------------
// Executive Client Read — counts.
// ---------------------------------------------------------------------------
//
// Trust rule (LBBW production-hardening, 2026-02): every executive section
// header count MUST equal the number of cards visibly rendered in that
// section. No "smart" cross-array fallback, no Type/category filtering — if
// the JSON authors a `topOpportunities`/`topThreats`/`topBlindSpots` array,
// the count and the render both use exactly that array. Other blind-spot
// arrays (`structuredBlindSpots`, `profileLevelBlindSpots`,
// `signalLinkedBlindSpots`) are surfaced in a SEPARATE "All detected blind
// spots" section with its own independent count, never mixed in here.
//
// `topBlindSpots` MUST NEVER be inflated by `structuredBlindSpots`.

const _renderableEntryCount = (arr) => {
    if (!Array.isArray(arr)) return 0;
    let n = 0;
    for (const raw of arr) {
        const e = normaliseNarrativeEntry(raw);
        if (!e || typeof e !== "object") continue;
        if (!hasContent(e.title) && !hasContent(e.summary)) continue;
        n += 1;
    }
    return n;
};

/** Strict executive opportunity count. Equals the number of cards visibly
 *  rendered in the "Top opportunities" section — entries are normalised
 *  (PascalCase ↔ lowerCamel) and dropped if they have neither title nor
 *  summary. `Type: "ADVANTAGE"` / `"DECISION_POINT"` items are NOT filtered
 *  out — the array is semantically opportunity-side and is rendered verbatim. */
export const countExecutiveOpportunities = ({ weeklyLens } = {}) => _renderableEntryCount(weeklyLens?.topOpportunities);

/** Strict executive threat count. Equals the number of cards visibly rendered
 *  in the "Top threats" section (normalised, with title or summary). */
export const countExecutiveThreats = ({ weeklyLens } = {}) => _renderableEntryCount(weeklyLens?.topThreats);

/** Strict executive blind-spot count. Equals the number of cards visibly
 *  rendered in the "Top blind spots" section. `structuredBlindSpots` /
 *  `profileLevelBlindSpots` / `signalLinkedBlindSpots` are surfaced
 *  separately in "All detected blind spots" and MUST NOT inflate this
 *  number. */
export const countExecutiveBlindSpots = ({ weeklyLens } = {}) => _renderableEntryCount(weeklyLens?.topBlindSpots);

/** Strict executive recommended-actions count. Counts entries with a usable
 *  action title after normalisation. Equals the number of cards visibly
 *  rendered in the "Where to act first" section. */
export const countExecutiveRecommendedActions = ({ weeklyLens } = {}) => {
    const arr = weeklyLens?.topRecommendedActions;
    if (!Array.isArray(arr)) return 0;
    let n = 0;
    for (const raw of arr) {
        const a = normaliseRecommendedAction(raw);
        if (a && hasContent(a.actionTitle)) n += 1;
    }
    return n;
};

// ---------------------------------------------------------------------------
// Partner ⇄ competitor overlap detection.
// Spec:
//   1. Trust explicit `partner.alsoCompetitor === true`.
//   2. Else normalised exact match against `evidence.relevantCompetitors`.
//   3. Small alias map for well-known legal-name variants.
//   4. NO generic substring containment.
// ---------------------------------------------------------------------------

const LEGAL_SUFFIX = /\b(ltd|inc|incorporated|corp|corporation|co|company|ag|gmbh|sa|s\.a\.|nv|n\.v\.|plc|llc|llp|group|holdings|holding|bank|se)\.?$/i;

/** Lowercase, trim, strip punctuation, collapse whitespace, strip
 *  well-known legal suffixes. */
export const normalizeCompanyName = (raw) => {
    if (raw == null) return "";
    let s = String(raw).toLowerCase().trim();
    if (!s) return "";
    // Strip punctuation except & and spaces
    s = s.replace(/[\u2013\u2014\-_/\\.,;:!?"'()\[\]{}]/g, " ");
    // Collapse spaces
    s = s.replace(/\s+/g, " ").trim();
    // Strip trailing legal suffix (one round only)
    s = s.replace(LEGAL_SUFFIX, "").trim();
    s = s.replace(/\s+/g, " ").trim();
    return s;
};

/** Alias clusters: each row maps to a single canonical key. Add only well-known
 *  expansions; avoid generic substrings. */
const ALIAS_CLUSTERS = [
    ["bny mellon", "bank of new york mellon", "bny", "bank of ny mellon"],
    ["jpmorgan", "jp morgan", "jpmorgan chase", "j p morgan", "jpmorgan chase bank"],
    ["state street", "state street corporation"],
    ["goldman sachs", "goldman sachs group"],
    ["bnp paribas", "bnp"],
    ["deutsche bank", "db"],
    ["société générale", "societe generale", "socgen"],
    ["dbs bank", "dbs"],
    ["hsbc holdings", "hsbc"],
    ["citigroup", "citi"],
    ["sumitomo mitsui", "smbc", "sumitomo mitsui banking corporation"],
];

const ALIAS_TO_CANON = (() => {
    const m = new Map();
    for (const cluster of ALIAS_CLUSTERS) {
        const canon = cluster[0];
        for (const v of cluster) m.set(normalizeCompanyName(v), canon);
    }
    return m;
})();

/** Resolve a name to its canonical key — alias map first, fallback to its
 *  own normalized form. */
export const canonicalNameKey = (raw) => {
    const norm = normalizeCompanyName(raw);
    return ALIAS_TO_CANON.get(norm) || norm;
};

/** Decide whether a partner is also flagged as a competitor.
 *  Returns { match: boolean, reason: "explicit" | "alias" | "exact" | null,
 *            via: matchedCompetitorName | null }. */
export const isPartnerAlsoCompetitor = (partner, competitors) => {
    if (!partner) return { match: false, reason: null, via: null };
    if (partner.alsoCompetitor === true) {
        return { match: true, reason: "explicit", via: null };
    }
    if (!Array.isArray(competitors) || competitors.length === 0) {
        return { match: false, reason: null, via: null };
    }
    const partnerKey = canonicalNameKey(partner.name);
    if (!partnerKey) return { match: false, reason: null, via: null };
    for (const comp of competitors) {
        if (comp == null) continue;
        const compKey = canonicalNameKey(comp);
        if (!compKey) continue;
        if (partnerKey === compKey) {
            const reason = ALIAS_TO_CANON.has(normalizeCompanyName(partner.name)) || ALIAS_TO_CANON.has(normalizeCompanyName(comp))
                ? "alias"
                : "exact";
            return { match: true, reason, via: comp };
        }
    }
    return { match: false, reason: null, via: null };
};
